import { logSantos, logWarn, devLog } from './logger';

// Función para obtener la sección correspondiente al día de la semana
export const getSectionForToday = () => {
  const today = new Date().getDay(); // 0 = Domingo, 1 = Lunes, etc.
  
  const weeklySchedule = {
    1: 'Buenos días',                    // Lunes
    2: 'Píldoras de San Agustín',       // Martes
    3: 'Habla San Agustín',             // Miércoles
    4: 'Orando con San Agustín',        // Jueves
    5: ['Historia y personajes', 'Sabías que…'], // Viernes (combinado)
    6: 'Reflexión agustiniana',         // Sábado
    0: 'Domingo con San Agustín'        // Domingo
  };
  
  return weeklySchedule[today];
};

// Función para obtener el título simplificado de la sección del día (modificada para priorizar santos)
export const getDailySectionTitle = async (allArticles = {}, fechaTest = null) => {
  // Primero verificar si hay santo del día
  const santoHoy = await checkSantoDelDia(allArticles, fechaTest);
  if (santoHoy) {
    return 'Santos Agustinos';
  }

  // Si no hay santo, usar lógica original
  const fecha = fechaTest || new Date();
  const today = fecha.getDay();
  
  if (today === 1) { // Lunes
    return 'Buenos días';
  } else {
    return 'Clave agustiniana';
  }
};

// Función para obtener la navegación del botón "Ver todos" (modificada para priorizar santos)
export const getDailySectionNavigation = async (allArticles = {}, fechaTest = null) => {
  // Primero verificar si hay santo del día
  const santoHoy = await checkSantoDelDia(allArticles, fechaTest);
  if (santoHoy) {
    return 'Santos Agustinos'; // Navegar a sección Santos Agustinos
  }

  // Si no hay santo, usar lógica original
  const fecha = fechaTest || new Date();
  const today = fecha.getDay();
  
  if (today === 1) { // Lunes
    return 'Buenos días'; // Navegar a sección Buenos días
  } else {
    return 'clave-agustiniana'; // Navegar a vista Clave agustiniana
  }
};

// Función para verificar si hoy hay un santo agustino (basada en SantoDelDia.js)
export const checkSantoDelDia = async (allArticles, fechaTest = null) => {
  logSantos('Ejecutándose checkSantoDelDia...');
  
  if (!allArticles || !allArticles['Santos Agustinos']) {
    logSantos('No hay datos de Santos Agustinos');
    return null;
  }

  let santos = allArticles['Santos Agustinos'];
  const fecha = fechaTest || new Date();
  const anio = fecha.getFullYear(); // Año en curso
  const mes = fecha.getMonth(); // 0-11
  const dia = fecha.getDate(); // 1-31
  
  logSantos(`Buscando santo para ${dia}/${mes + 1}/${anio} en ${santos.length} santos`);
  
  // Si tenemos pocos santos (menos de 30), intentar cargar la sección completa
  if (santos.length < 30 && window.agustinosDataLoader && window.agustinosDataLoader.cargarSeccionCompleta) {
    logSantos('Pocos santos cargados, intentando cargar sección completa...');
    try {
      const santosCompletos = await window.agustinosDataLoader.cargarSeccionCompleta('Santos Agustinos', 500);
      if (santosCompletos.length > santos.length) {
        santos = santosCompletos;
        logSantos(`Sección completa cargada: ${santos.length} santos`);
        
        // Actualizar los datos en allArticles para uso futuro
        allArticles['Santos Agustinos'] = santos;
      }
    } catch (error) {
      logWarn('Error cargando sección completa de santos, usando datos parciales:', error);
    }
  }
  
  // Debug: mostrar algunos santos (solo en desarrollo)
  logSantos(`Santos disponibles: ${santos.length} total`);
  
  // Si hay muchos santos, mostrar algunos del mes actual para debug
  if (santos.length > 10) {
    const santosDelMes = santos.filter(santo => {
      const fechaSanto = new Date(santo.date);
      return fechaSanto.getFullYear() === anio && fechaSanto.getMonth() === mes;
    });
    
    if (santosDelMes.length > 0) {
      logSantos(`Santos del mes ${mes + 1}/${anio} encontrados: ${santosDelMes.length}`);
    }
  }

  // Buscar santo que coincida con día, mes Y año en curso
  const santoEncontrado = santos.find(santo => {
    if (!santo.date) return false;
    
    const fechaSanto = new Date(santo.date);
    const coincide = fechaSanto.getFullYear() === anio && fechaSanto.getMonth() === mes && fechaSanto.getDate() === dia;
    
    if (coincide) {
      logSantos(`¡Coincidencia encontrada! ${santo.title} - ${fechaSanto.getDate()}/${fechaSanto.getMonth() + 1}/${fechaSanto.getFullYear()}`);
    }
    
    return coincide;
  });

  if (santoEncontrado) {
    const fechaStr = fechaTest ? fechaTest.toLocaleDateString('es-ES') : 'hoy';
    logSantos(`🎉 Santo del día detectado para ${fechaStr}: ${santoEncontrado.title}`);
    return santoEncontrado;
  }

  logSantos(`No se encontró santo para ${dia}/${mes + 1}`);
  return null;
};

// Función de testing para probar diferentes fechas
export const testSantoDelDia = async (allArticles, mes, dia) => {
  const fechaTest = new Date(2024, mes - 1, dia); // mes-1 porque Date usa 0-11
  devLog(`🧪 Testing santo para ${dia}/${mes}:`);
  
  const santo = await checkSantoDelDia(allArticles, fechaTest);
  const titulo = await getDailySectionTitle(allArticles, fechaTest);
  const navegacion = await getDailySectionNavigation(allArticles, fechaTest);
  
  devLog(`  📋 Título de sección: "${titulo}"`);
  devLog(`  🔗 Navegación: "${navegacion}"`);
  
  if (santo) {
    devLog(`  👼 Santo encontrado: ${santo.title}`);
    devLog(`  📅 Fecha original: ${santo.date}`);
  } else {
    devLog(`  ❌ No hay santo para esta fecha`);
  }
  
  return { santo, titulo, navegacion };
};

// Función de debug para usar desde la consola del navegador
export const debugSantosPrioridad = async () => {
  const appData = window.agustinosAppData;
  if (!appData || !appData.articles) {
    devLog('❌ No hay datos de la app cargados. Asegúrate de que la app esté completamente cargada.');
    return;
  }

  devLog('🧪 === TESTING PRIORIDAD DE SANTOS ===');
  
  // Mostrar algunos santos disponibles
  const santos = appData.articles['Santos Agustinos'] || [];
  devLog(`📋 Santos disponibles: ${santos.length}`);
  
  if (santos.length > 0) {
    devLog('📅 Primeros 5 santos con sus fechas:');
    santos.slice(0, 5).forEach((santo, i) => {
      const fecha = new Date(santo.date);
      devLog(`  ${i+1}. ${santo.title} - ${fecha.getDate()}/${fecha.getMonth()+1}`);
    });
    
    // Probar fecha de hoy
    devLog('\n🎯 Testing fecha de hoy:');
    await testSantoDelDia(appData.articles, new Date().getMonth() + 1, new Date().getDate());
    
    // Probar algunas fechas conocidas con santos
    devLog('\n🧪 Testing fechas con santos conocidos:');
    for (const santo of santos.slice(0, 3)) {
      const fecha = new Date(santo.date);
      await testSantoDelDia(appData.articles, fecha.getMonth() + 1, fecha.getDate());
    }
  }
  
  devLog('\n💡 Uso: await testSantoDelDia(window.agustinosAppData.articles, mes, dia)');
  devLog('💡 Ejemplo: await testSantoDelDia(window.agustinosAppData.articles, 9, 22)');
};

// Función para obtener los artículos de la sección del día (modificada para priorizar santos)
export const getDailySectionArticles = async (allArticles) => {
  // Primero verificar si hay santo del día
  const santoHoy = await checkSantoDelDia(allArticles);
  if (santoHoy) {
    // Si hay santo del día, retornar ese artículo
    return [santoHoy];
  }

  // Si no hay santo, usar lógica original
  const todaySections = getSectionForToday();
  
  if (Array.isArray(todaySections)) {
    // Para viernes: combinar Historia y personajes + Sabías que
    const articles = [];
    todaySections.forEach(section => {
      if (allArticles[section] && allArticles[section].length > 0) {
        articles.push(...allArticles[section]);
      }
    });
    // Ordenar por fecha más reciente y tomar el primero
    return articles.sort((a, b) => new Date(b.date) - new Date(a.date));
  } else {
    // Para otros días: una sección específica
    return allArticles[todaySections] || [];
  }
};